<template>
  <div class="studio-chart-wrapper selectchart" :class="chartWrapperClasses">
    <div v-if="loading">
      <ScreenLoader />
    </div>
    <div v-else>
      <div v-if="sampleError" class="text-center">{{ sampleError }}</div>
      <div v-else>
        <div class='main-chart pa-0'> 
          <div class="chart-name-top">
            <h6>{{ chartName }}</h6>
          </div>
          <div :ref="chartId"></div>
        </div>
        <v-dialog v-model="isModalOpen" class="multi-chart-preview-div dashboard-chart-preview composition">
          <v-card class="position-relative studio-card studio-custom-card ">
            <v-card-text>
              
                <div class="chart-name-top w-100">
            <h6>{{ chartName }}</h6>
            <div class="modal-tab-div">
                <div v-if="chartType === 'Line Plot' || chartType === 'Scatter Plot'"
                  class="tab-div justify-space-between">
                  <p>X Axis Scale</p>
                  <v-btn-toggle v-model="isModalXLog" class="axis-toggle setHeight" mandatory
                    @update:modelValue="handleModalXScaleToggleChange">
                    <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                    <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                  </v-btn-toggle>
                </div>
                <div v-if="chartType === 'Line Plot' || chartType === 'Scatter Plot'"
                  class="tab-div  justify-space-between">
                  <p>Y Axis Scale</p>
                  <v-btn-toggle v-model="isModalYLog" class="axis-toggle setHeight" mandatory
                    @update:modelValue="handleModalYScaleToggleChange">
                    <v-btn class="switch-btn" :value="true" outlined>Log</v-btn>
                    <v-btn class="switch-btn" :value="false" outlined>Linear</v-btn>
                  </v-btn-toggle>
                </div>
              </div>
          </div>
          <div :ref="modalChartId"></div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import ScreenLoader from "../Loaders/ScreenLoader.vue";
import screenWidthMixin from "../../../assets/JS/screenWidthMixin";

import downloadIcon from '../../../assets/Svg/Chart/csv_chart.svg';
import Expand from "../../../assets/Svg/Chart/expand-btn.svg";

import { eventBus } from "../../../eventBus.js";
import { mapGetters } from "vuex";
import { formatNumber, shouldFormatNumbers, createPlotlyTickFormat, createCustomTickFormatter } from "../../../utils/numberFormatter";

export default {
  name: "DynamicChart",
  components: {
    ScreenLoader
  },
  mixins: [screenWidthMixin],
  props: {
    loading: { type: Boolean, default: true },
    chartName: { type: String, default: "" },
    chartType: { type: String, required: true },
    xAxisData: { type: Array, default: () => [] },
    yAxisData: { 
      type: Array, 
      default: () => []
    },
    zAxisData: { type: Array, default: () => [] },
    xColorGroupData: { type: Array, default: [] },
    yColorGroupData: { type: Array, default: [] },
    zColorGroupData: { type: Array, default: [] },
    colorGroupKey: { type: String, default: "" },
    colorGroupDisplayName: { type: String, default: "" },
    sampleError: { type: String, default: "" },
    markerSizes: Array,
    xAxisDisplayName: { type: String, default: "" },
    yAxisDisplayNames: { type: Array, default: () => [] },
    zAxisDisplayName: { type: String, default: "" },
    xAxisCaption: { type: Array, default: () => [] },
    yAxisCaption: { type: Array, default: () => [] },
    xBins: { type: Number },
    yBins: { type: Number },
    xAxisScale: { type: String, default: "linear" },
    yAxisScale: { type: String, default: "linear" },
    enablePropagationSort: { type: Boolean, default: true }, // New prop to control propagation sorting
    // New props for studio screen mode and filter state
    isCreateEditMode: { type: Boolean, default: false },
    isFilterOpen: { type: Boolean, default: false },
    // Raw table data for comprehensive CSV export
    tableData: { type: Array, default: () => [] },
    // Actual property names for dynamic column detection
    xAxisColumn: { type: String, default: "" },
    yAxisColumns: { type: Array, default: () => [] },
    zAxisColumn: { type: String, default: "" },
    // Decimal precision configuration
    xAxisDecimalPrecision: { type: Number, default: 2 },
    yAxisDecimalPrecision: { type: Number, default: 2 },
    zAxisDecimalPrecision: { type: Number, default: 2 },
  },
  data() {
    
    return {
      chartId: "dynamicChart",
      modalChartId: "modalChart",
        chartHeight: this.calculateChartHeight(),
      isModalOpen: false,
      hasValidXColorGroupData: false,
      hasValidYColorGroupData: false,
      previousSidebarState: null,
      cross: require("../../../assets/Svg/Chart/chart-cross.svg"),
      camIcon: require("../../../assets/Svg/camIcon.svg"),
      xAxisScaleModal: this.xAxisScale, // Initialize with prop value
      yAxisScaleModal: this.yAxisScale, // Initialize with prop value
      isModalXLog: this.xAxisScale === "log", // Set initial value based on the prop
      isModalYLog: this.yAxisScale === "log", // Set initial value based on the prop
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    chartWrapperClasses() {
      return {
        'chart-wrapper': true
      };
    }
  },
  watch: {
    xAxisData: {
      handler(newVal, oldVal) {
        // Check if we're switching from edit to create mode (data being cleared)
        if (this.isCreateEditMode && oldVal && oldVal.length > 0 && (!newVal || newVal.length === 0)) {
          // Data is being cleared, likely switching to create mode
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        } else {
          this.plotChart();
        }
      }
    },
    yAxisData: {
      handler(newVal, oldVal) {
        // Check if we're switching from edit to create mode (data being cleared)
        if (this.isCreateEditMode && oldVal && oldVal.length > 0 && (!newVal || newVal.length === 0)) {
          // Data is being cleared, likely switching to create mode
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        } else {
          this.plotChart();
        }
      }
    },
    zAxisData: {
      handler(newVal, oldVal) {
        // Check if we're switching from edit to create mode (data being cleared)
        if (this.isCreateEditMode && oldVal && oldVal.length > 0 && (!newVal || newVal.length === 0)) {
          // Data is being cleared, likely switching to create mode
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        } else {
          this.plotChart();
        }
      }
    },
    xColorGroupData: {
      handler() {
        this.plotChart();
      }
    },
    yColorGroupData: {
      handler() {
        this.plotChart();
      }
    },
    zColorGroupData: {
      handler() {
        this.plotChart();
      }
    },
    chartType: {
      handler(newVal, oldVal) {
        // Check if we're switching from edit to create mode (chart type being reset)
        if (this.isCreateEditMode && oldVal && oldVal !== newVal) {
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        } else {
          this.plotChart();
        }
      }
    },
    chartName: {
      handler(newVal, oldVal) {
        // Check if we're switching from edit to create mode (chart name being cleared)
        if (this.isCreateEditMode && oldVal && oldVal.trim() !== '' && (!newVal || newVal.trim() === '')) {
          // Chart name is being cleared, likely switching to create mode
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        }
      }
    },
    colorGroupKey: {
      handler(newVal, oldVal) {
        // Check if we're switching from edit to create mode (color grouping being reset)
        if (this.isCreateEditMode && oldVal && oldVal.trim() !== '' && (!newVal || newVal.trim() === '')) {
          // Color grouping is being reset, likely switching to create mode
          this.clearChartData();
          this.$nextTick(() => {
            this.plotChart();
          });
        }
      }
    },
    xBins: {
      handler() {
        this.plotChart();
      }
    },
    yBins: {
      handler() {
        this.plotChart();
      }
    },
    xAxisScale(newValue) {
      this.xAxisScaleModal = newValue;
      this.isModalXLog = newValue === "log";
      this.plotChart();
    },
    yAxisScale(newValue) {
      this.yAxisScaleModal = newValue;
      this.isModalYLog = newValue === "log";
      this.plotChart();
    },

    // Watch for studio-specific prop changes
    isCreateEditMode: {
      handler(newVal, oldVal) {
        // Check for edit to create mode transition
        this.$nextTick(() => {
          this.detectEditToCreateTransition();
        });
      }
    },
    isFilterOpen: {
      handler() {
        this.plotChart();
      }
    },
    // Watch for modal state changes to recalculate dimensions
    isModalOpen: {
      handler(newVal) {
        if (newVal) {
          // Modal opened - recalculate dimensions and plot
          this.$nextTick(() => {
            setTimeout(() => {
              this.plotModalChart();
            }, 150);
          });
        }
      }
    },
    // Watch for dark mode changes to reload plots and update colors
    isDarkMode() {
      this.plotChart();
    }
    },
    mounted() {
      // Add window resize listener to recalculate chart height
      this.handleResize = () => {
        this.chartHeight = this.calculateChartHeight();
        this.$nextTick(() => {
          this.plotChart();
        });
      };
      window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
      // Remove window resize listener
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
    },
    methods: {
      calculateChartHeight() {
        // Calculate chart height based on screen size
        const screenHeight = window.innerHeight;
        const screenWidth = window.innerWidth;
        
        // Get zoom level (approximate)
        const zoomLevel = window.devicePixelRatio || 1;
        
        // Base height calculation - more conservative to account for zoom
        let baseHeight = 500;
        
        // Adjust height based on screen size with zoom consideration
        if (screenHeight >= 1080) {
          // Large screens (1080p and above) - reduce height to account for zoom
          baseHeight = Math.min(screenHeight * 0.5, 700); 
        } else if (screenHeight >= 768) {
          // Medium screens (768p to 1079p)
          baseHeight = Math.min(screenHeight * 0.45, 550); 
        } else {
          // Small screens (below 768p)
          baseHeight = Math.min(screenHeight * 0.4, 450); 
        }
        
        // Further reduce height if zoom level is high
        if (zoomLevel > 1) {
          baseHeight = baseHeight * 0.9; 
        }
        
        // Ensure minimum height
        return Math.max(baseHeight, 300); 
      },
      calculateModalChartHeight() {
        // Calculate modal chart height based on screen size
        const screenHeight = window.innerHeight;
        const screenWidth = window.innerWidth;
        
        // Get zoom level (approximate)
        const zoomLevel = window.devicePixelRatio || 1;
        let baseHeight = 600;
        
        if (screenHeight >= 1080) {
          baseHeight = Math.min(screenHeight * 0.6, 800); 
        } else if (screenHeight >= 768) {
          // Medium screens (768p to 1079p)
          baseHeight = Math.min(screenHeight * 0.55, 650); 
        } else {
          // Small screens (below 768p)
          baseHeight = Math.min(screenHeight * 0.5, 550); 
        }
        
        // Further reduce height if zoom level is high
        if (zoomLevel > 1) {
          baseHeight = baseHeight * 0.9; 
        }
        
        // Ensure minimum height for modal
        return Math.max(baseHeight, 380); 
      },
    // Method to clear chart data when new API calls are made
    clearChartData() {
      // Clear any existing plot with proper cleanup
      if (this.$refs[this.chartId]) {
        try {
          // Purge the plot to clean up internal state
          Plotly.purge(this.$refs[this.chartId]);
        } catch (error) {
          console.warn('Error purging chart:', error);
        }
      }
      if (this.$refs[this.modalChartId]) {
        try {
          Plotly.purge(this.$refs[this.modalChartId]);
        } catch (error) {
          console.warn('Error purging modal chart:', error);
        }
      }
      
      // Reset modal scale values to prevent carryover from edit mode
      this.xAxisScaleModal = this.xAxisScale;
      this.yAxisScaleModal = this.yAxisScale;
      this.isModalXLog = this.xAxisScale === "log";
      this.isModalYLog = this.yAxisScale === "log";
      
      // Reset internal flags to prevent carryover from edit mode
      this.hasValidXColorGroupData = false;
      this.hasValidYColorGroupData = false;
    },
    
    // Method to force clear chart when switching from edit to create mode
    forceClearForModeSwitch() {
      // Clear all chart data and reset to initial state
      this.clearChartData();
      
      // Reset all internal flags
      this.lastModeState = null;
      
      // Force a fresh plot
      this.$nextTick(() => {
        this.plotChart();
      });
    },
    
    // Method to detect and handle edit to create mode transition
    detectEditToCreateTransition() {
      // Check if we're transitioning from edit to create mode
      const isTransitioning = this.isCreateEditMode && 
                             this.chartName && this.chartName.trim() !== '' &&
                             (!this.xAxisData || this.xAxisData.length === 0) &&
                             (!this.yAxisData || this.yAxisData.length === 0);
      
      if (isTransitioning) {
        // We're transitioning from edit to create mode
        this.forceClearForModeSwitch();
      }
    },

    getUOMForProperty(columnName) {
      // Get UOM from the first row of table data if available
      if (this.tableData && this.tableData.length > 0) {
        const firstRow = this.tableData[0];
        const uomKey = `${columnName}_UOM`;
        if (firstRow && firstRow[uomKey] && firstRow[uomKey] !== 'data-not-available') {
          return firstRow[uomKey];
        }
      }
      return null;
    },

    formatAxisTitle(displayName, uom) {
      // Format axis title with UOM if available
      if (uom && uom.trim() !== '') {
        return `${displayName} (${uom})`;
      }
      return displayName;
    },

    // Check if axis data should be formatted with k/M/B notation
    shouldFormatAxisData(axisType) {
      if (!this.tableData || this.tableData.length === 0) {
        return false;
      }

      let columnName;
      if (axisType === 'x') {
        columnName = this.xAxisColumn;
      } else if (axisType === 'y') {
        columnName = this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null;
      } else if (axisType === 'z') {
        columnName = this.zAxisColumn;
      }

      if (!columnName) {
        return false;
      }

      const values = this.tableData
        .map(item => item[columnName])
        .filter(value => typeof value === 'number' && !isNaN(value));

      const shouldFormat = shouldFormatNumbers(values);
      return shouldFormat;
    },

    // Get formatted tick format for Plotly
    getFormattedTickFormat(axisType) {
      let decimalPrecision = 2; // Default precision
      
      if (axisType === 'x' && this.xAxisDecimalPrecision !== undefined) {
        decimalPrecision = this.xAxisDecimalPrecision;
      } else if (axisType === 'y' && this.yAxisDecimalPrecision !== undefined) {
        decimalPrecision = this.yAxisDecimalPrecision;
      } else if (axisType === 'z' && this.zAxisDecimalPrecision !== undefined) {
        decimalPrecision = this.zAxisDecimalPrecision;
      }


      // For 2D histograms, use 0 decimal places to remove .00 from tick labels
      if (this.chartType === "2D Histogram") {
        decimalPrecision = 0;
      }

      // For large numbers, use 0 decimal places to avoid showing .00k
      if (this.shouldFormatAxisData(axisType)) {
        decimalPrecision = 0;
      }

      if (this.shouldFormatAxisData(axisType)) {
        // Use custom formatting function for large numbers
        return createCustomTickFormatter(decimalPrecision);
      }
      
      // Use more explicit formatting for better decimal control
      return `.${decimalPrecision}~f`;
    },
    handleModalXScaleToggleChange() {
      this.xAxisScaleModal = this.isModalXLog ? "log" : "linear";
      // Re-plot the modal chart with the new scale
      this.plotModalChart();
    },
    handleModalYScaleToggleChange() {
      this.yAxisScaleModal = this.isModalYLog ? "log" : "linear";
      // Re-plot the modal chart with the new scale
      this.plotModalChart();
    },
    downloadCSV() {
      // If we have raw table data, use comprehensive CSV format like dashboard
      if (this.tableData && this.tableData.length > 0) {
        const csvContent = this.convertToCsv(this.tableData, this.chartName);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        
        // Create a download link
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `${this.chartName || "chart"}.csv`);
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // Fallback to basic CSV format if no table data available
      const rows = [];

      // Add header row - include Z-axis if it exists
      const headers = [this.xAxisDisplayName, ...this.yAxisDisplayNames];
      if (this.zAxisDisplayName && this.zAxisData && this.zAxisData.length > 0) {
        headers.push(this.zAxisDisplayName);
      }
      rows.push(headers.join(","));

      // Determine the maximum length for iteration - include Z-axis data length
      const maxLength = Math.max(
        this.xAxisData.length, 
        this.yAxisData.reduce((max, arr) => Math.max(max, arr.length), 0),
        this.zAxisData ? this.zAxisData.length : 0
      );

      for (let i = 0; i < maxLength; i++) {
        // Get the x value, defaulting to an empty string if undefined
        const x = this.xAxisData[i] !== undefined ? this.xAxisData[i] : "";

        // Collect the y values for this row
        const yValues = this.yAxisData.map(yDataArray => {
          return yDataArray[i] !== undefined ? yDataArray[i] : "";
        });

        // Collect the z value for this row if Z-axis exists
        const z = (this.zAxisData && this.zAxisData[i] !== undefined) ? this.zAxisData[i] : "";

        // Add data row with x, y, and z values
        const rowData = [x, ...yValues];
        if (this.zAxisDisplayName && this.zAxisData && this.zAxisData.length > 0) {
          rowData.push(z);
        }
        rows.push(rowData.join(","));
      }

      // Create CSV Blob
      const csvContent = rows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      // Create a download link
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${this.chartName || "chart"}.csv`);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    convertToCsv(data, chartName) {
      if (!data || data.length === 0) {
        return '';
      }

      const csvRows = [];
      const headers = ['Sample ID', 'Asset', 'Field', 'Well', 'Fluid Sample ID'].concat(Object.keys(data[0]).filter(header => header !== 'caption' && header !== '_id' && header !== 'uniqueID' && !header.endsWith('_decimal') && header !== 'field' && header !== 'asset' && header !== 'well'));
      csvRows.push(headers.join(","));

      for (const row of data) {
        const values = headers.map((header) => {
          if (['Asset', 'Field', 'Well', 'Fluid Sample ID'].includes(header)) {
            return row.caption.split(',')[headers.indexOf(header) - 1];
          }
          if (header === 'Sample ID') {
            return row._id;
          }
          return JSON.stringify(row[header], (key, value) => value === null ? "" : value);
        });
        csvRows.push(values.join(","));
      }

      return csvRows.join("\n");
    },

    downloadPNG() {
      if (!this.$refs[this.chartId]) {
        return;
      }

      // Create a temporary div for the download
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      document.body.appendChild(tempDiv);

      // Get the current traces and create a temporary layout with title
      const traces = this.generateTraces();
      const layoutWithTitle = this.generateLayout();
      layoutWithTitle.title = {
        text: this.chartName,
        font: {
          size: 16,
          color: '#333'
        },
        x: 0.5,
        y: 0.95
      };

      // Create the temporary plot with title
      Plotly.newPlot(tempDiv, traces, layoutWithTitle, {
        displayModeBar: false,
        responsive: true
      }).then(() => {
        // Download the PNG with title included
        Plotly.downloadImage(tempDiv, {
          format: 'png',
          filename: this.chartName || 'chart',
          height: 600,
          width: 800
        }).then(() => {
          // Clean up the temporary div
          document.body.removeChild(tempDiv);
        });
      });
    },
    plotChart() {
      
      this.$nextTick(() => {
        if (!this.$refs[this.chartId]) {
          return;
        }

        // Clear previous chart data to prevent mixing data from different API calls
        this.clearChartData();
        
        // Check if we have valid data to plot
        const hasXData = this.xAxisData && this.xAxisData.length > 0;
        const hasYData = this.yAxisData && this.yAxisData.length > 0;
        const isHistogram = this.chartType === "Histogram";
        
        // For histograms, only X-axis data is required
        // For other charts, both X and Y axis data are required
        const hasValidData = hasXData && (isHistogram || hasYData);

        if (!hasValidData) {
          // Show a blank chart with the same layout and config as the real chart
          const layout = this.generateLayout();
          const config = this.generateConfig();
          
          // Clear any data-dependent properties
          if (this.chartType === "3D Scatter") {
            // Handle 3D scatter plot layout
            if (layout.scene && layout.scene.xaxis) {
              layout.scene.xaxis.title = { text: this.xAxisDisplayName || "X Axis" };
            }
            if (layout.scene && layout.scene.yaxis) {
              layout.scene.yaxis.title = { text: (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ") : "Y Axis") };
            }
            if (layout.scene && layout.scene.zaxis) {
              layout.scene.zaxis.title = { text: this.zAxisDisplayName || "Z Axis" };
            }
          } else {
            // Handle 2D chart layout
            if (layout.xaxis) {
              layout.xaxis.title = { text: this.xAxisDisplayName || "" };
            }
            if (layout.yaxis) {
              layout.yaxis.title = { text: (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ") : "") };
            }
          }
          layout.showlegend = false;
          
          Plotly.newPlot(this.$refs[this.chartId], [], layout, config);
          return;
        }

        const traces = this.generateTraces();
        const layout = this.generateLayout();
        const config = this.generateConfig();

        // Validate that traces were generated
        if (!traces || traces.length === 0) {
          // Show a blank chart with the same layout and config as the real chart
          const layout = this.generateLayout();
          const config = this.generateConfig();
          
          // Clear any data-dependent properties
          layout.xaxis.title = { text: "" };
          layout.yaxis.title = { text: "" };
          layout.showlegend = false;
          
          Plotly.newPlot(this.$refs[this.chartId], [], layout, config);
          return;
        }

        // Create the new plot with responsive configuration
        Plotly.newPlot(this.$refs[this.chartId], traces, layout, config)
          .then(() => {
            // Force Plotly to resize to fill the container
            Plotly.Plots.resize(this.$refs[this.chartId]);
            this.setupsidebarStateCheck();
          })
          .catch((error) => {
            console.error('❌ DynamicChart: Error creating plot:', error);
          });
      });
    },
    plotModalChart() {
      if (!this.$refs[this.modalChartId]) {
        return;
      }


      // For histograms, only X-axis data is required
      // For other charts, both X and Y axis data are required
      const isHistogram = this.chartType === "Histogram";
      if (!isHistogram && (!this.yAxisData || this.yAxisData.length === 0)) {
        // Clear the existing modal plot when there's no Y-axis data for non-histogram charts
        try {
          Plotly.purge(this.$refs[this.modalChartId]);
        } catch (error) {
          console.warn('Error purging modal chart:', error);
        }
        return;
      }

      const traces = this.generateTraces();
      const config = this.generateModalConfig();
      
      // Create a completely separate layout for modal chart to avoid conflicts
      const layout = this.generateModalLayout();

      this.$nextTick(() => {
        try {
          // Clear existing modal plot first
          Plotly.purge(this.$refs[this.modalChartId]);
          

          Plotly.newPlot(this.$refs[this.modalChartId], traces, layout, config)
            .then(() => {
              // Force Plotly to resize to fill the container
              Plotly.Plots.resize(this.$refs[this.modalChartId]);
            })
            .catch((error) => {
              console.error('Error creating modal plot:', error);
            });
        } catch (error) {
          console.error('Error plotting modal chart:', error);
        }
      });
    },

    generateTraces() {
      const traceType = this.getTraceType();
      const traceMode = this.getTraceMode();
      const scatterMarkerSizes = 8;
      const scatter3DMarkerSizes = 6; // 3D scatter plot marker size

      // Validate data availability - histograms only need xAxisData
      const hasXData = this.xAxisData && this.xAxisData.length > 0;
      const hasYData = this.yAxisData && this.yAxisData.length > 0;
      const isHistogram = this.chartType === "Histogram";
      
      if (!hasXData) {
        return [];
      }

      // For non-histogram charts, also check yAxisData
      if (!isHistogram && !hasYData) {
        return [];
      }

      // Direct check for Line Plot - use dashboard logic for consistency
      if (this.chartType === "Line Plot" && this.tableData && this.tableData.length > 0 && this.xAxisColumn && this.yAxisColumns) {
        
        // Check if we have a color group (legend) selected
        const hasColorGroup = this.colorGroupKey && this.colorGroupKey.trim() !== '';
        
        if (hasColorGroup) {
          // Group by _id first, then by legend for color assignment
          const idGroups = {};
          
          this.tableData.forEach((row, index) => {
            const dataId = row.uniqueID;
            const legend = row[this.colorGroupKey];
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = {
                data: [],
                legend: legend
              };
            }
            
            // Use the actual column names from the data row
            const xVal = this.xAxisColumn && row[this.xAxisColumn] !== undefined ? row[this.xAxisColumn] : 
                        (this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : null);
            
            // Use actual property names from props for dynamic column detection
            let yVal = null;
            let matchingKey = null;
            if (this.yAxisColumns && this.yAxisColumns.length > 0) {
              // Use the actual property name from yAxisColumns
              matchingKey = this.yAxisColumns[0];
              yVal = row[matchingKey];
            } else if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
              // Fallback: try to find column by display name matching
              const yDisplayName = this.yAxisDisplayNames[0];
              const availableKeys = Object.keys(row);
              
              // Filter out only obvious system columns that should never be used as Y-axis data
              const systemColumns = ['_id', 'caption', 'field', 'asset', 'well', 'fluidSampleID'];
              const dataColumns = availableKeys.filter(key => 
                !systemColumns.includes(key) && 
                !key.endsWith('_UOM') && 
                !key.endsWith('_decimal') &&
                !key.endsWith('_uom') &&  // lowercase version
                !key.endsWith('_DECIMAL') // uppercase version
              );
              
              let matchingKey = dataColumns.find(key => {
                const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
                const normalizedDisplay = yDisplayName.toLowerCase().replace(/[_\s-]/g, '');
                return normalizedKey === normalizedDisplay || 
                       normalizedKey.includes(normalizedDisplay) || 
                       normalizedDisplay.includes(normalizedKey);
              });
              
              // If no match found in data columns, try all available keys as fallback
              if (!matchingKey && dataColumns.length === 0) {
                matchingKey = availableKeys.find(key => {
                  const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
                  const normalizedDisplay = yDisplayName.toLowerCase().replace(/[_\s-]/g, '');
                  return normalizedKey === normalizedDisplay || 
                         normalizedKey.includes(normalizedDisplay) || 
                         normalizedDisplay.includes(normalizedKey);
                });
              }
              
              if (matchingKey) {
                yVal = row[matchingKey];
              } else {
                // Final fallback: try to find any numeric column that could be Y-axis data
                const availableKeys = Object.keys(row);
                const systemColumns = ['_id', 'caption', 'field', 'asset', 'well', 'fluidSampleID', 'uniqueID'];
                const dataColumns = availableKeys.filter(key => 
                  !systemColumns.includes(key) && 
                  !key.endsWith('_UOM') && 
                  !key.endsWith('_decimal') &&
                  !key.endsWith('_uom') &&  
                  !key.endsWith('_DECIMAL') &&
                  row[key] !== null && 
                  row[key] !== undefined && 
                  row[key] !== ''
                );
                
                // Try to find a column with numeric data
                for (const colName of dataColumns) {
                  const value = row[colName];
                  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
                    yVal = value;
                    matchingKey = colName;
                    break;
                  }
                }
              }
            }
            
            const zVal = row[this.zAxisColumn] || row[this.zAxisDisplayName] || null;
          
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              // Only convert to number if it's a numeric string, otherwise keep as string
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              idGroups[dataId].data.push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
            }
          });
          
          // Create traces for each unique _id with legend colors
          const traces = [];
          const colors = [
            "rgba(0, 100, 250, 0.8)",   // Blue
            "rgba(255, 99, 71, 0.8)",   // Red
            "rgba(50, 205, 50, 0.8)",   // Green
            "rgba(255, 215, 0, 0.8)",   // Gold
            "rgba(147, 112, 219, 0.8)", // Purple
            "rgba(255, 165, 0, 0.8)"    // Orange
          ];
          
          // Create a color mapping for each legend type
          const legendColorMap = {};
          let colorIndex = 0;
          const shownLegends = new Set();
          
          Object.keys(idGroups).forEach((idKey) => {
            const idGroup = idGroups[idKey];
            const validData = idGroup.data;
            const legend = idGroup.legend;
            
            if (validData.length === 0) {
              return;
            }

            // Assign color based on legend type, not individual traces
            if (!legendColorMap[legend]) {
              legendColorMap[legend] = colors[Object.keys(legendColorMap).length % colors.length];
            }
            const traceColor = legendColorMap[legend];

            // Track which legend types we've already shown
            const isFirstOfType = !shownLegends.has(legend);
            if (isFirstOfType) {
              shownLegends.add(legend);
            }

            // Sort data by x-axis values - only sort numeric data, preserve order for strings
            const sortedData = validData.sort((a, b) => {
              // If both are numbers, sort numerically
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              // For non-numeric data, preserve original order
              return 0;
            });
            const validXData = sortedData.map(item => item.x);
            const validYData = sortedData.map(item => item.y);
            const validZData = sortedData.map(item => item.z);

            const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

            const trace = {
              x: validXData,
              y: processedYData,
              z: validZData,
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: traceColor // Use the same color for all traces of the same legend type
              },
              name: legend, // Use legend type as name
              legendgroup: legend, // Group by legend type
              showlegend: isFirstOfType, // Show only one legend entry per type
              connectgaps: false,
              customdata: validXData.map((xValue, dataIndex) => {
                // Use caption from the original row data like Line Plot
                const item = sortedData[dataIndex];
                return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
            shownLegends.add(legend);
            colorIndex++;
          });

          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        } else {
          // No color group - group by _id only with same color
          const idGroups = {};
          
          this.tableData.forEach((row, index) => {
            const dataId = row.uniqueID;
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = [];
            }
            
            // Use the actual column names from the data row
            const xVal = this.xAxisColumn && row[this.xAxisColumn] !== undefined ? row[this.xAxisColumn] : 
                        (this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : null);
            
            // Use actual property names from props for dynamic column detection
            let yVal = null;
            let matchingKey = null;
            if (this.yAxisColumns && this.yAxisColumns.length > 0) {
              // Use the actual property name from yAxisColumns
              matchingKey = this.yAxisColumns[0];
              yVal = row[matchingKey];
            } else if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
              // Fallback: try to find column by display name matching
              const yDisplayName = this.yAxisDisplayNames[0];
              const availableKeys = Object.keys(row);
              
              // Filter out only obvious system columns that should never be used as Y-axis data
              const systemColumns = ['_id', 'caption', 'field', 'asset', 'well', 'fluidSampleID'];
              const dataColumns = availableKeys.filter(key => 
                !systemColumns.includes(key) && 
                !key.endsWith('_UOM') && 
                !key.endsWith('_decimal') &&
                !key.endsWith('_uom') &&  // lowercase version
                !key.endsWith('_DECIMAL') // uppercase version
              );
              
              let matchingKey = dataColumns.find(key => {
                const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
                const normalizedDisplay = yDisplayName.toLowerCase().replace(/[_\s-]/g, '');
                return normalizedKey === normalizedDisplay || 
                       normalizedKey.includes(normalizedDisplay) || 
                       normalizedDisplay.includes(normalizedKey);
              });
              
              // If no match found in data columns, try all available keys as fallback
              if (!matchingKey && dataColumns.length === 0) {
                matchingKey = availableKeys.find(key => {
                  const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
                  const normalizedDisplay = yDisplayName.toLowerCase().replace(/[_\s-]/g, '');
                  return normalizedKey === normalizedDisplay || 
                         normalizedKey.includes(normalizedDisplay) || 
                         normalizedDisplay.includes(normalizedKey);
                });
              }
              
              if (matchingKey) {
                yVal = row[matchingKey];
              } else {
                // Final fallback: try to find any numeric column that could be Y-axis data
                const availableKeys = Object.keys(row);
                const systemColumns = ['_id', 'caption', 'field', 'asset', 'well', 'fluidSampleID', 'uniqueID'];
                const dataColumns = availableKeys.filter(key => 
                  !systemColumns.includes(key) && 
                  !key.endsWith('_UOM') && 
                  !key.endsWith('_decimal') &&
                  !key.endsWith('_uom') &&  
                  !key.endsWith('_DECIMAL') &&
                  row[key] !== null && 
                  row[key] !== undefined && 
                  row[key] !== ''
                );
                
                // Try to find a column with numeric data
                for (const colName of dataColumns) {
                  const value = row[colName];
                  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
                    yVal = value;
                    matchingKey = colName;
                    break;
                  }
                }
              }
            }
            
            const zVal = row[this.zAxisColumn] || row[this.zAxisDisplayName] || null;
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              // Only convert to number if it's a numeric string, otherwise keep as string
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              idGroups[dataId].push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
            }
          });
          
          // Create traces for each unique _id with same color
          const traces = [];
          const singleColor = "rgba(0, 100, 250, 0.8)"; // Same blue color for all
          
          Object.keys(idGroups).forEach((idKey) => {
            const validData = idGroups[idKey];
            
            if (validData.length === 0) {
              return;
            }

            // Sort data by x-axis values - only sort numeric data, preserve order for strings
            const sortedData = validData.sort((a, b) => {
              // If both are numbers, sort numerically
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              // For non-numeric data, preserve original order
              return 0;
            });
            const validXData = sortedData.map(item => item.x);
            const validYData = sortedData.map(item => item.y);
            const validZData = sortedData.map(item => item.z);

            const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

            const trace = {
              x: validXData,
              y: processedYData,
              z: validZData,
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: singleColor
              },
              name: `Data Series ${Object.keys(idGroups).indexOf(idKey) + 1}`,
              showlegend: false,
              connectgaps: false,
              customdata: validXData.map((xValue, dataIndex) => {
                // Use caption from the original row data like Line Plot
                const item = sortedData[dataIndex];
                return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
          });

          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        }
      }

      // Handle Line Plot when tableData is available but column names might not be set
      if (this.chartType === "Line Plot" && this.tableData && this.tableData.length > 0 && (!this.xAxisColumn || !this.yAxisColumns)) {
        // Use tableData with dynamic column detection for Line Plot
        const traces = [];
        
        // Check if we have a color group (legend) selected
        const hasColorGroup = this.colorGroupKey && this.colorGroupKey.trim() !== '';
        
        if (hasColorGroup) {
          // Group by uniqueID first, then by legend for color assignment
          const idGroups = {};
          
          this.tableData.forEach((row, index) => {
            const dataId = row.uniqueID;
            const legend = row[this.colorGroupKey];
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = {
                data: [],
                legend: legend
              };
            }
            
            // Use dynamic column detection for X and Y values
            const xVal = this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : 
                        (this.xAxisData && this.xAxisData[index] !== undefined ? this.xAxisData[index] : null);
            
            let yVal = null;
            if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 && row[this.yAxisDisplayNames[0]] !== undefined) {
              yVal = row[this.yAxisDisplayNames[0]];
            } else if (this.yAxisData && this.yAxisData[0] && this.yAxisData[0][index] !== undefined) {
              yVal = this.yAxisData[0][index];
            }
            
            // Include 0 values explicitly - only exclude null, undefined, and empty strings
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
             
              
              idGroups[dataId].data.push({ x: processedXVal, y: yVal, originalRow: row });
            }
          });
          
          // Create traces for each unique ID with legend colors
          const colors = [
            "rgba(0, 100, 250, 0.8)",   // Blue
            "rgba(255, 99, 71, 0.8)",   // Red
            "rgba(50, 205, 50, 0.8)",   // Green
            "rgba(255, 215, 0, 0.8)",   // Gold
            "rgba(147, 112, 219, 0.8)", // Purple
            "rgba(255, 165, 0, 0.8)"    // Orange
          ];
          
          const legendColorMap = {};
          const shownLegends = new Set();
          
          Object.keys(idGroups).forEach((idKey) => {
            const idGroup = idGroups[idKey];
            const validData = idGroup.data;
            const legend = idGroup.legend;
            
            if (validData.length === 0) {
              return;
            }

            if (!legendColorMap[legend]) {
              legendColorMap[legend] = colors[Object.keys(legendColorMap).length % colors.length];
            }
            const traceColor = legendColorMap[legend];

            const isFirstOfType = !shownLegends.has(legend);
            if (isFirstOfType) {
              shownLegends.add(legend);
            }

            // Sort data by x-axis values - only sort numeric data, preserve order for strings
            const sortedData = validData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              // For non-numeric data, preserve original order
              return 0;
            });

            const trace = {
              x: sortedData.map(item => item.x),
              y: sortedData.map(item => item.y),
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: traceColor
              },
              name: isFirstOfType ? legend : undefined,
              showlegend: isFirstOfType,
              connectgaps: false,
              line: {
                color: traceColor,
                width: 2
              },
              customdata: sortedData.map(item => {
                // Use caption from the original row data
                return item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}<br>
%{y}<br>
%{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
          });

          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        } else {
          // No color group - group by uniqueID only with same color
          const idGroups = {};
          
          this.tableData.forEach((row, index) => {
            const dataId = row.uniqueID;
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = [];
            }
            
            // Use dynamic column detection for X and Y values
            const xVal = this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : 
                        (this.xAxisData && this.xAxisData[index] !== undefined ? this.xAxisData[index] : null);
            
            let yVal = null;
            if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 && row[this.yAxisDisplayNames[0]] !== undefined) {
              yVal = row[this.yAxisDisplayNames[0]];
            } else if (this.yAxisData && this.yAxisData[0] && this.yAxisData[0][index] !== undefined) {
              yVal = this.yAxisData[0][index];
            }
            
            // Include 0 values explicitly - only exclude null, undefined, and empty strings
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              
              idGroups[dataId].push({ x: processedXVal, y: yVal, originalRow: row });
            }
          });
          
          // Create traces for each unique ID
          Object.keys(idGroups).forEach((idKey) => {
            const validData = idGroups[idKey];
            
            if (validData.length === 0) {
              return;
            }

            // Sort data by x-axis values - only sort numeric data, preserve order for strings
            const sortedData = validData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              // For non-numeric data, preserve original order
              return 0;
            });

            const trace = {
              x: sortedData.map(item => item.x),
              y: sortedData.map(item => item.y),
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: "rgba(0, 100, 250, 0.8)"
              },
              showlegend: false,
              connectgaps: false,
              line: {
                color: "rgba(0, 100, 250, 0.8)",
                width: 2
              },
              customdata: sortedData.map(item => {
                // Use caption from the original row data
                return item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}<br>
%{y}<br>
%{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
          });

          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        }
      }

      // Fallback for Line Plot when tableData is not available or column names are not provided
      if (this.chartType === "Line Plot" && this.xAxisData && this.yAxisData && this.xAxisData.length > 0 && this.yAxisData.length > 0 && !this.tableData) {
        // Use the standard Line Plot logic with xAxisData and yAxisData arrays
        const traces = [];
        
        // Check if we have color grouping
        const hasValidColorGroup = this.hasValidXColorGroupData && this.hasValidYColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '';
        
        if (hasValidColorGroup) {
          // Use color grouping logic
          const colors = [
            "rgba(0, 100, 250, 0.8)",   // Blue
            "rgba(255, 99, 71, 0.8)",   // Red
            "rgba(50, 205, 50, 0.8)",   // Green
            "rgba(255, 215, 0, 0.8)",   // Gold
            "rgba(147, 112, 219, 0.8)", // Purple
            "rgba(255, 165, 0, 0.8)"    // Orange
          ];
          
          // Group data by color group
          const groupedData = {};
          this.xAxisData.forEach((xVal, index) => {
            const yVal = this.yAxisData[0] && this.yAxisData[0][index] !== undefined ? this.yAxisData[0][index] : null;
            const colorGroupValue = this.xColorGroupData[Object.keys(this.xColorGroupData)[0]] && 
                                   this.xColorGroupData[Object.keys(this.xColorGroupData)[0]][index] ? 
                                   this.xColorGroupData[Object.keys(this.xColorGroupData)[0]][index] : 'Default';
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              if (!groupedData[colorGroupValue]) {
                groupedData[colorGroupValue] = [];
              }
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              groupedData[colorGroupValue].push({ x: processedXVal, y: yVal });
            }
          });
          
          // Create traces for each color group
          Object.keys(groupedData).forEach((groupKey, index) => {
            const groupData = groupedData[groupKey];
            if (groupData.length === 0) return;
            
            // Sort data by x-axis values
            const sortedData = groupData.sort((a, b) => {
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              if (typeof a.x === 'string' && typeof b.x === 'string') {
                return a.x.localeCompare(b.x);
              }
              return String(a.x).localeCompare(String(b.x));
            });
            
            const trace = {
              x: sortedData.map(item => item.x),
              y: sortedData.map(item => item.y),
              type: 'scatter',
              mode: 'lines',
              marker: {
                size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
                color: colors[index % colors.length]
              },
              name: groupKey,
              showlegend: true,
              connectgaps: false,
              customdata: sortedData.map((item, dataIndex) => {
                // Use caption from the original row data like Line Plot
                return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
            };
            
            traces.push(trace);
          });
          
          const validTraces = traces.filter(trace => trace !== null);
          return validTraces;
        } else {
          // No color grouping - create single trace
          const validData = [];
          this.xAxisData.forEach((xVal, index) => {
            const yVal = this.yAxisData[0] && this.yAxisData[0][index] !== undefined ? this.yAxisData[0][index] : null;
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              validData.push({ x: processedXVal, y: yVal });
            }
          });
          
          if (validData.length === 0) {
            return [];
          }
          
          // Sort data by x-axis values - only sort numeric data, preserve order for strings
          const sortedData = validData.sort((a, b) => {
            if (typeof a.x === 'number' && typeof b.x === 'number') {
              return a.x - b.x;
            }
            // For non-numeric data, preserve original order
            return 0;
          });
          
          const trace = {
            x: sortedData.map(item => item.x),
            y: sortedData.map(item => item.y),
            type: 'scatter',
            mode: 'lines',
            marker: {
              size: (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
              color: 'rgba(0, 100, 250, 0.8)'
            },
            name: this.yAxisDisplayNames[0] || 'Y-Axis',
            showlegend: false,
            connectgaps: false,
            customdata: sortedData.map((item, dataIndex) => {
              // Use caption from the original row data like Line Plot
              return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
            }),
            hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
          };
          
          return [trace];
        }
      }

      this.hasValidXColorGroupData = Object.values(this.xColorGroupData).some(group => Array.isArray(group) && group.length > 0);
      this.hasValidYColorGroupData = Object.values(this.yColorGroupData).some(group => Array.isArray(group) && group.length > 0);

      // Only use color grouping if a color group is actually selected
      const hasColorGroupSelected = this.colorGroupKey && this.colorGroupKey.trim() !== '';
      
      // For histograms, we only need xColorGroupData for legends
      const hasValidColorGroupForHistogram = this.chartType === "Histogram" 
        ? this.hasValidXColorGroupData 
        : (this.hasValidXColorGroupData && this.hasValidYColorGroupData);

      if (this.chartType === "Histogram") {
        // Define colors for different groups
        const colors = [
          "rgba(0, 100, 250, 0.6)",   // Blue
          "rgba(255, 99, 71, 0.6)",   // Red
          "rgba(50, 205, 50, 0.6)",   // Green
          "rgba(255, 215, 0, 0.6)",   // Gold
          "rgba(147, 112, 219, 0.6)", // Purple
          "rgba(255, 165, 0, 0.6)"    // Orange
        ];
        const borderColor = "rgba(0, 0, 0, 1)";
        const borderWidth = 1;

        // If there is grouping by color AND a color group is selected
        if (hasValidColorGroupForHistogram && hasColorGroupSelected) {
          // Get sorted legend keys to ensure consistent color assignment
          const sortedLegendKeys = Object.keys(this.xColorGroupData).sort();
          
          // Calculate unified bin boundaries for ALL groups to ensure consistent binning
          let unifiedXbinsConfig = undefined;
          let isNumericData = false;
          
          // First, collect all data from all groups to calculate unified bin boundaries
          const allXData = [];
          for (const key of sortedLegendKeys) {
            const xData = this.xColorGroupData[key];
            const validXData = xData.filter(value => 
              value !== null && 
              value !== undefined && 
              value !== ''
            );
            allXData.push(...validXData);
          }
          
          if (allXData.length > 0) {
            // Check if all data is numeric
            isNumericData = allXData.every(value => {
              if (typeof value === 'number') {
                return !isNaN(value);
              }
              if (typeof value === 'string') {
                const num = parseFloat(value);
                return !isNaN(num) && isFinite(num);
              }
              return false;
            });
            
            if (isNumericData) {
              // Convert string numbers to actual numbers
              const numericData = allXData.map(value => {
                if (typeof value === 'string') {
                  return parseFloat(value);
                }
                return value;
              });
              
              const minVal = Math.min(...numericData);
              const maxVal = Math.max(...numericData);
              const range = maxVal - minVal;
              
            if (range > 0) {
              // Ensure minimum value starts from 0 for positive data
              const adjustedMinVal = Math.max(0, minVal);
              const adjustedRange = maxVal - adjustedMinVal;
              
              // Add small padding to ensure all data points are included
              const padding = adjustedRange * 0.001; // 0.1% padding
              const binSize = (adjustedRange + 2 * padding) / this.xBins;
              
              unifiedXbinsConfig = {
                start: Math.max(0, adjustedMinVal - padding), // Ensure start is never negative
                end: maxVal + padding,
                size: binSize
              };
            }
            }
          }
          
          // Now create traces for each legend group using the grouped data
          return sortedLegendKeys.map((key, index) => {
            const xData = this.xColorGroupData[key];
            // Filter out invalid data - allow both strings and numbers
            const validXData = xData.filter(value => 
              value !== null && 
              value !== undefined && 
              value !== ''
            );
            
            if (validXData.length === 0) {
              return null;
            }
            
            let sortedData;
            
            if (isNumericData) {
              // Convert string numbers to actual numbers and sort for consistent plotting
              sortedData = validXData.map(value => {
                if (typeof value === 'string') {
                  return parseFloat(value);
                }
                return value;
              }).sort((a, b) => a - b);
            } else {
              // For string data, don't sort and use categorical bins
              sortedData = validXData;
            }
            
            return {
              x: sortedData,
              type: traceType,
              autobinx: false,
              mode: traceMode,
              name: key,
              nbinsx: this.xBins, // Use exact number of bins
              xbins: unifiedXbinsConfig, // Use unified bin boundaries for all groups
              marker: {
                color: colors[index % colors.length],
                line: {
                  color: borderColor,
                  width: borderWidth
                }
              },
              customdata: sortedData.map((value, dataIndex) => {
                // For histograms with legends, use the legend group name as caption
                return key; // Return the legend group name (e.g., "FD", "ML", "AI", "AGI")
              }),
              hovertemplate: `
  <b>${key}</b><br>
  Range: %{x}<br>
  Count: %{y}
  <extra></extra>
`,
            };
          }).filter(trace => trace !== null);
        }
        // No color‐grouping, single histogram
        else {
          // Filter out invalid data - allow both strings and numbers
          const validXData = this.xAxisData.filter(value => 
            value !== null && 
            value !== undefined && 
            value !== ''
          );
          
          if (validXData.length === 0) {
            return [];
          }
          
          // Check if data is numeric for sorting and binning
          // More robust numeric detection - handle strings that can be converted to numbers
          const isNumericData = validXData.every(value => {
            if (typeof value === 'number') {
              return !isNaN(value);
            }
            if (typeof value === 'string') {
              const num = parseFloat(value);
              return !isNaN(num) && isFinite(num);
            }
            return false;
          });
          
          let sortedData;
          
          if (isNumericData) {
            // Convert string numbers to actual numbers and sort for consistent plotting
            sortedData = validXData.map(value => {
              if (typeof value === 'string') {
                return parseFloat(value);
              }
              return value;
            }).sort((a, b) => a - b);
          } else {
            // For string data, don't sort and use categorical bins
            sortedData = validXData;
          }
          
          // Calculate exact bin boundaries to ensure precise bin count
          let xbinsConfig = undefined;
          if (isNumericData && sortedData.length > 0) {
            const minVal = Math.min(...sortedData);
            const maxVal = Math.max(...sortedData);
            const range = maxVal - minVal;
            
            if (range > 0) {
              // Ensure minimum value starts from 0 for positive data
              const adjustedMinVal = Math.max(0, minVal);
              const adjustedRange = maxVal - adjustedMinVal;
              
              // Add small padding to ensure all data points are included
              const padding = adjustedRange * 0.001; // 0.1% padding
              const binSize = (adjustedRange + 2 * padding) / this.xBins;
              
              xbinsConfig = {
                start: Math.max(0, adjustedMinVal - padding), // Ensure start is never negative
                end: maxVal + padding,
                size: binSize
              };
            }
          } else if (!isNumericData) {
            // Only for truly categorical data, use nbinsx without xbins
            xbinsConfig = undefined;
          }
          
          return [{
            x: sortedData,
            type: traceType,
            name: this.chartName || "Histogram",
            autobinx: false,
            nbinsx: this.xBins, // Use exact number of bins
            xbins: xbinsConfig, // Use calculated bin boundaries
            marker: {
              color: colors[0],
              line: {
                color: borderColor,
                width: borderWidth
              }
            },
            // Remove customdata for simple histograms to avoid caption display
            hovertemplate: `
  Range: %{x}<br>
  Count: %{y}
  <extra></extra>
`,
          }];
        }
      } else if (hasValidColorGroupForHistogram && hasColorGroupSelected) {
        // For Line Plot only, use _id grouping to prevent spaghetti lines
        if (this.chartType === "Line Plot") {
        // Define colors for different groups (same as histogram)
        const colors = [
          "rgba(0, 100, 250, 0.6)",   // Blue
          "rgba(255, 99, 71, 0.6)",   // Red
          "rgba(50, 205, 50, 0.6)",   // Green
          "rgba(255, 215, 0, 0.6)",   // Gold
          "rgba(147, 112, 219, 0.6)", // Purple
          "rgba(255, 165, 0, 0.6)"    // Orange
        ];
        
        // Generate traces by grouping primarily by _id, then by legend for color assignment
        // This approach ensures each unique _id gets its own trace regardless of legend
        const idGroups = {};
        
        // Group tableData by _id first (primary grouping)
        this.tableData.forEach((row) => {
          const dataId = row.uniqueID;
          const legend = row[this.colorGroupKey];
          
          if (!idGroups[dataId]) {
            idGroups[dataId] = {
              data: [],
              legend: legend
            };
          }
          
          // Use the actual column names from the API response
          // Use dynamic column detection instead of hardcoded properties
          const xVal = this.xAxisColumn && row[this.xAxisColumn] !== undefined ? row[this.xAxisColumn] : 
                      (this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : null);
          const yVal = this.yAxisColumns && this.yAxisColumns.length > 0 && row[this.yAxisColumns[0]] !== undefined ? row[this.yAxisColumns[0]] : 
                      (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 && row[this.yAxisDisplayNames[0]] !== undefined ? row[this.yAxisDisplayNames[0]] : null);
          const zVal = row[this.zAxisDisplayName] || null;
          
          if (xVal !== null && xVal !== undefined && xVal !== '' && 
              yVal !== null && yVal !== undefined && yVal !== '') {
            
            // Convert xVal to number if it's a numeric string, otherwise keep as string
            let processedXVal = xVal;
            if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
              processedXVal = parseFloat(xVal);
            }
            
            idGroups[dataId].data.push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
          }
        });
        
      
        
        // Create traces array to store all traces
        const traces = [];
        let colorIndex = 0;
        const shownLegends = new Set(); // Track which legends we've already shown
        
        // Create a trace for each unique _id
        Object.keys(idGroups).forEach((idKey, index) => {
          const idGroup = idGroups[idKey];
          const validData = idGroup.data;
          const legend = idGroup.legend;
          const markerSize = (this.chartType === "Scatter Plot") ? scatterMarkerSizes : 
                             (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes;
          
          if (validData.length === 0) {
            return;
          }

          // Sort data by x-axis values to ensure proper propagation pattern
          // For numeric data: sort x-axis ascending (lower values first) for proper propagation
          // For string data: preserve original order
          const isXNumeric = validData.every(item => typeof item.x === 'number' && !isNaN(item.x));
          let sortedData;
          
          if (isXNumeric) {
            if (this.enablePropagationSort) {
              // Sort by x-axis ascending for proper propagation pattern (lower values first)
              sortedData = validData.sort((a, b) => {
                // If both are numbers, sort numerically
                if (typeof a.x === 'number' && typeof b.x === 'number') {
                  return a.x - b.x;
                }
                // For non-numeric data, preserve original order
                return 0;
              });
              
              // For certain chart types, we might want to ensure y-axis follows propagation pattern
              if (this.chartType === "Line Plot" || this.chartType === "Scatter Plot") {
                // Additional check: if y-axis values are decreasing as x increases, 
                // this might indicate a reverse propagation pattern
                const firstY = sortedData[0]?.y;
                const lastY = sortedData[sortedData.length - 1]?.y;
                
                // If y-values are decreasing, we might want to reverse the order
                // This is optional and can be controlled by a prop if needed
                if (firstY !== undefined && lastY !== undefined && firstY > lastY) {
                  // This indicates a decreasing trend - keep as is for proper propagation
                  // The data is already sorted correctly for propagation visualization
                }
              }
            } else {
              // If propagation sorting is disabled, preserve original order
              sortedData = validData;
            }
          } else {
            // For string data, preserve original order
            sortedData = validData;
          }
          
          const validXData = sortedData.map(item => item.x);
          const validYData = sortedData.map(item => item.y);
          const validZData = sortedData.map(item => item.z);

          const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

          const trace = {
            x: validXData,
            y: processedYData,
            z: validZData,
            type: traceType,
            mode: traceMode,
            // Only set marker for non-Line Plot charts, or for Line Plot with legend
            ...(this.chartType !== 'Line Plot' || (this.chartType === 'Line Plot' && hasValidColorGroupForHistogram && hasColorGroupSelected) ? {
            marker: {
              size: markerSize,
              color: colors[colorIndex % colors.length], // Use consistent color assignment
              ...(this.chartType === 'Histogram' ? {
                line: {
                  color: 'rgba(0, 0, 0, 1)',     // Solid black border
                  width: 1                       // Border width
                }
              } : {})
              }
            } : {}),
            name: legend, // Use the legend from the _id group
            legendgroup: legend, // Group by legend for proper legend behavior
            showlegend: !shownLegends.has(legend), // Show legend only for the first trace of each legend group
            // Add connectgaps: false to prevent connecting points across gaps
            connectgaps: false,

            customdata: validXData.map((xValue, dataIndex) => {
              // Use caption from the original row data like Line Plot
              const item = sortedData[dataIndex];
              return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
            }),

            hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
          };

          if (this.chartType === "Bubble" || this.chartType === "3D Scatter") {
            const maxMarkerSize = Math.max(...this.markerSizes);
            trace.marker = {
              ...trace.marker,
              sizemode: 'area',
              sizeref: 2.0 * maxMarkerSize / (80 * 2), // Adjusted size reference for better fitting
              sizemin: 2,
            };
          }

          if (this.chartType === "2D Histogram") {
            trace.colorscale = [
              [0.0, 'rgba(247,251,255,255)'],
              [0.25, 'rgba(193,217,237,255)'],
              [0.5, 'rgba(97,167,210,255)'],
              [0.75, 'rgba(24,101,172,255)'],
              [1.0, 'rgba(8,48,108,255)']
            ]
            trace.colorbar = {
              title: {
                text: 'Number of Samples', // Title for the color bar
                side: 'right',
              }
            };
          }

          if (["2D Histogram", "Bubble", "Heatmap"].includes(this.chartType)) {
            trace.autobinx = false;
            trace.autobiny = false;
            
            // Only set bins for numeric data
            const isXNumeric = validXData.every(val => typeof val === 'number' && !isNaN(val));
            const isYNumeric = processedYData.every(val => typeof val === 'number' && !isNaN(val));
            
            if (isXNumeric) {
              trace.xbins = { 
                size: this.calculateBinSize(validXData, this.xBins),
                start: Math.min(...validXData),
                end: Math.max(...validXData)
              };
            }
            
            if (isYNumeric) {
              trace.ybins = { 
                size: this.calculateBinSize(processedYData, this.yBins),
                start: Math.min(...processedYData),
                end: Math.max(...processedYData)
              };
            }
          }

          traces.push(trace);
          
          // Mark this legend as shown
          shownLegends.add(legend);
          colorIndex++;
        });

      return traces.filter(trace => trace !== null);
        } else {
          // For other chart types, use original grouping by legend
          return Object.keys(this.xColorGroupData).map((key) => {
            const xData = this.xColorGroupData[key];
            let yData = this.yColorGroupData[key];
            const zData = this.zColorGroupData[key];
            const markerSizes = [...zData];

            if (!Array.isArray(xData) || !Array.isArray(yData) || xData.length !== yData.length) {
              return null;
            }

            // Filter out invalid data points
            const validData = xData.map((xVal, i) => {
              const yVal = yData[i];
              if (xVal !== null && xVal !== undefined && xVal !== '' && 
                  yVal !== null && yVal !== undefined && yVal !== '') {
                return { x: xVal, y: yVal, index: i };
              }
              return null;
            }).filter(item => item !== null);

            if (validData.length === 0) {
              return null;
            }

            // Sort data by x-axis values - only sort numeric data, preserve order for strings
            const sortedData = validData.sort((a, b) => {
              // If both are numbers, sort numerically
              if (typeof a.x === 'number' && typeof b.x === 'number') {
                return a.x - b.x;
              }
              // For non-numeric data, preserve original order
              return 0;
            });

            yData = sortedData.map(item => Array.isArray(item.y) ? item.y[0] : item.y);

            const markerSize = (this.chartType === "Scatter Plot")
              ? (scatterMarkerSizes)
              : (this.chartType === "3D Scatter")
              ? (scatter3DMarkerSizes)
              : (markerSizes);

            const trace = {
              x: sortedData.map(item => item.x),
              y: yData,
              z: zData,
              type: traceType,
              mode: traceMode,
              name: key,
              marker: {
                size: markerSize
              },
              connectgaps: false,
            };

            if (this.chartType === "Bubble" || this.chartType === "3D Scatter") {
              const maxMarkerSize = Math.max(...markerSizes);
              trace.marker = {
                ...trace.marker,
                sizemode: 'area',
                sizeref: 2.0 * maxMarkerSize / (80 * 2),
                sizemin: 2,
              };
            }

            if (["2D Histogram"].includes(this.chartType)) {
              trace.autobinx = false;
              trace.autobiny = false;
              trace.xbins = { 
                size: this.calculateBinSize(sortedData.map(item => item.x), this.xBins),
                start: Math.min(...sortedData.map(item => item.x)),
                end: Math.max(...sortedData.map(item => item.x))
              };
              trace.ybins = { 
                size: this.calculateBinSize(yData, this.yBins),
                start: Math.min(...yData),
                end: Math.max(...yData)
              };
            }

            return trace;
          }).filter(trace => trace !== null);
        }
    }
    // Handle case when no legend is selected but we still want to group by _id
    else if (this.tableData && this.tableData.length > 0) {
      // For Line Plot only, group by _id to prevent spaghetti lines even without legend
      if (this.chartType === "Line Plot") {
      // Group by _id to prevent spaghetti lines even without legend
      const idGroups = {};
      
      this.tableData.forEach((row) => {
        const dataId = row.uniqueID;
        
        if (!idGroups[dataId]) {
          idGroups[dataId] = [];
        }
        
        // Use dynamic column detection instead of hardcoded properties
        const xVal = this.xAxisColumn && row[this.xAxisColumn] !== undefined ? row[this.xAxisColumn] : 
                    (this.xAxisDisplayName && row[this.xAxisDisplayName] !== undefined ? row[this.xAxisDisplayName] : null);
        const yVal = this.yAxisColumns && this.yAxisColumns.length > 0 && row[this.yAxisColumns[0]] !== undefined ? row[this.yAxisColumns[0]] : 
                    (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 && row[this.yAxisDisplayNames[0]] !== undefined ? row[this.yAxisDisplayNames[0]] : null);
        const zVal = row[this.zAxisDisplayName] || null;
        
        if (xVal !== null && xVal !== undefined && xVal !== '' && 
            yVal !== null && yVal !== undefined && yVal !== '') {
          
          let processedXVal = xVal;
          // Only convert to number if it's a numeric string, otherwise keep as string
          if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
            processedXVal = parseFloat(xVal);
          }
          
          idGroups[dataId].push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
        }
      });
      
      // Create traces for each unique _id with same color
      const traces = [];
      const singleColor = "rgba(0, 100, 250, 0.8)"; // Same blue color for all
      
      Object.keys(idGroups).forEach((idKey) => {
        const validData = idGroups[idKey];
        
        if (validData.length === 0) {
          return;
        }

        // Sort data by x-axis values - only sort numeric data, preserve order for strings
        const sortedData = validData.sort((a, b) => {
          // If both are numbers, sort numerically
          if (typeof a.x === 'number' && typeof b.x === 'number') {
            return a.x - b.x;
          }
          // For non-numeric data, preserve original order
          return 0;
        });
        const validXData = sortedData.map(item => item.x);
        const validYData = sortedData.map(item => item.y);
        const validZData = sortedData.map(item => item.z);

        const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

        // Create trace based on chart type
        let traceType = 'scatter';
        let traceMode = 'markers';
        
        if (this.chartType === "Line Plot") {
          traceMode = 'lines+markers';
        } else if (this.chartType === "Scatter Plot") {
          traceMode = 'markers';
        }

        const trace = {
          x: validXData,
          y: processedYData,
          z: validZData,
          type: traceType,
          mode: traceMode,
          marker: {
            size: (this.chartType === "Scatter Plot") ? scatterMarkerSizes : 
                  (this.chartType === "3D Scatter") ? scatter3DMarkerSizes : this.markerSizes,
            color: singleColor
          },
          name: `Data Series ${Object.keys(idGroups).indexOf(idKey) + 1}`, // Generic name
          showlegend: false, // No legend since it's not really a legend
          connectgaps: false,
          customdata: validXData.map((xValue, dataIndex) => {
            // Use caption from the original row data like Line Plot
            const item = sortedData[dataIndex];
            return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
          }),
          hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
        };

        traces.push(trace);
      });

      return traces.filter(trace => trace !== null);
      } else {
        // For other chart types without legend, use the original logic
        return this.yAxisData.map((yData, index) => {
          const xData = this.xAxisData;
          const zData = this.zAxisData;

          // Check if any of the data arrays are invalid
          const isInvalidData = (data) => !data || data.every(value => value === null || value === undefined);

          if (isInvalidData(xData) || isInvalidData(yData)) {
            return null; // Skip plotting for invalid data
          }

          // Now that we have raw data, we need to filter and pair the data properly
          // Create paired data points, filtering out invalid entries
          // The data should be mapped according to the latest API response structure
          const validData = [];
          const minLength = Math.min(xData.length, yData.length);
          
          for (let i = 0; i < minLength; i++) {
            const xVal = xData[i];
            const yVal = yData[i];
            
            // Filter out invalid data points - allow both strings and numbers in both xData and yData
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '') {
              
              // Convert xVal to number if it's a numeric string, otherwise keep as string
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal)) && isFinite(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              validData.push({ x: processedXVal, y: yVal, index: i });
            }
          }

          if (validData.length === 0) {
            return null;
          }

          // For non-legend charts, preserve the original order to maintain caption alignment
          // Only sort if explicitly requested for propagation patterns
          let sortedData;
          if (this.enablePropagationSort) {
            // Sort data by x-axis values to ensure proper propagation pattern
            // For numeric data: sort x-axis ascending (lower values first) for proper propagation
            // For string data: preserve original order
          const isXNumeric = validData.every(item => typeof item.x === 'number' && !isNaN(item.x));
            
            if (isXNumeric) {
              // Sort by x-axis ascending for proper propagation pattern (lower values first)
              sortedData = validData.sort((a, b) => {
                // If both are numbers, sort numerically
                if (typeof a.x === 'number' && typeof b.x === 'number') {
                  return a.x - b.x;
                }
                // For non-numeric data, preserve original order
                return 0;
              });
              
              // For certain chart types, we might want to ensure y-axis follows propagation pattern
              if (this.chartType === "Line Plot" || this.chartType === "Scatter Plot") {
                // Additional check: if y-axis values are decreasing as x increases, 
                // this might indicate a reverse propagation pattern
                const firstY = sortedData[0]?.y;
                const lastY = sortedData[sortedData.length - 1]?.y;
                
                // If y-values are decreasing, we might want to reverse the order
                // This is optional and can be controlled by a prop if needed
                if (firstY !== undefined && lastY !== undefined && firstY > lastY) {
                  // This indicates a decreasing trend - keep as is for proper propagation
                  // The data is already sorted correctly for propagation visualization
                }
              }
            } else {
              // For string data, preserve original order
              sortedData = validData;
            }
          } else {
            // If propagation sorting is disabled, preserve original order to maintain caption alignment
            sortedData = validData;
          }
          
          const validXData = sortedData.map(item => item.x);
          const validYData = sortedData.map(item => item.y);

          const markerSize = (this.chartType === "Scatter Plot")
            ? (scatterMarkerSizes)
            : (this.chartType === "3D Scatter")
            ? (scatter3DMarkerSizes)
            : (this.markerSizes);

          const trace = {
            x: validXData,
            y: validYData,
            z: zData,
            type: traceType,
            mode: traceMode,
            // Add connectgaps: false to prevent connecting points across gaps
            connectgaps: false,
            // Set marker for all chart types, with blue color for all plots without legend (except histogram)
            marker: {
              size: markerSize,
              ...(this.chartType !== 'Histogram' && !(hasValidColorGroupForHistogram && hasColorGroupSelected) ? {
                color: 'rgba(0, 100, 250, 0.8)' // Blue color for all plots without legend (except histogram)
              } : {}),
              ...(this.chartType === 'Histogram' ? {
                color: 'rgba(0, 100, 250, 0.6)', // Blue with some transparency
                line: {
                  color: 'rgba(0, 0, 0, 1)',     // Solid black border
                  width: 1                       // Border width
                }
              } : {})
            },
            name: this.yAxisDisplayNames[index] || `Y-Axis ${index + 1}`,
            customdata: validXData && validXData.length > 0
              ? validXData.map((xValue, dataIndex) => {
                // For non-legend charts, use original index from sortedData to get correct caption
                const originalIndex = sortedData[dataIndex].index;
                if (this.xAxisCaption && this.xAxisCaption.length > 0 && originalIndex < this.xAxisCaption.length) {
                  return this.xAxisCaption[originalIndex];
                }
                return 'N/A';
              })
              : validYData.map((yValue, i) => {
                // For non-legend charts, use original index from sortedData to get correct caption
                const originalIndex = sortedData[i].index;
                if (this.yAxisCaption && this.yAxisCaption.length > 0 && originalIndex < this.yAxisCaption.length) {
                  return this.yAxisCaption[originalIndex];
                }
                return 'N/A';
              }),

            hovertemplate: `
  %{x}  %{y}  %{customdata}
  <extra></extra>
`,
          };
          if (this.chartType === "Bubble" || this.chartType === "3D Scatter") {
            const maxMarkerSize = Math.max(...this.markerSizes);
            trace.marker = {
              ...trace.marker,
              sizemode: 'area',
              sizeref: 2.0 * maxMarkerSize / (80 * 2), // Adjusted size reference for better fitting
              sizemin: 2,
            };
          }

          if (this.chartType === "2D Histogram") {
            trace.colorscale = [
              [0.0, 'rgba(247,251,255,255)'],
              [0.25, 'rgba(193,217,237,255)'],
              [0.5, 'rgba(97,167,210,255)'],
              [0.75, 'rgba(24,101,172,255)'],
              [1.0, 'rgba(8,48,108,255)']
            ];
            trace.colorbar = {
              title: {
                text: 'Number of Samples', // Title for the color bar
                side: 'right',
              }
            };
          }

          if (["2D Histogram", "Bubble", "Heatmap"].includes(this.chartType)) {
            trace.autobinx = false;
            trace.autobiny = false;
            
            // Only set bins for numeric data
            const isXNumeric = validXData.every(val => typeof val === 'number' && !isNaN(val));
            const isYNumeric = validYData.every(val => typeof val === 'number' && !isNaN(val));
            
            if (isXNumeric) {
              trace.xbins = { 
                size: this.calculateBinSize(validXData, this.xBins),
                start: Math.min(...validXData),
                end: Math.max(...validXData)
              };
            }
            
            if (isYNumeric) {
              trace.ybins = { 
                size: this.calculateBinSize(validYData, this.yBins),
                start: Math.min(...validYData),
                end: Math.max(...validYData)
              };
            }
          }

          // Return the trace if valid, else skip it
          return trace;
        }).filter(trace => trace !== null); // Ensure no null traces are included in the final array
      }
    }
    },
    generateLayout() {
      // Check if there are legends to determine right margin
      // For histograms, only check xColorGroupData; for other charts, check both
      // For Line Plot with tableData, check if colorGroupKey is set
      const hasLegends = this.chartType === "Histogram" 
        ? (this.hasValidXColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '')
        : this.chartType === "Line Plot" && this.tableData && this.tableData.length > 0
          ? (this.colorGroupKey && this.colorGroupKey.trim() !== '')
          : (this.hasValidXColorGroupData && this.hasValidYColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '');
      const rightMargin = hasLegends ? 120 : 50;
      
      // Simple responsive layout
      const baseLayout = {
        autosize: true,
        height: this.chartHeight || 500,
        margin: { l: 50, r: rightMargin, b: 50, t: 50 },
        legend: {
          title: {
            text: hasLegends ? (this.colorGroupDisplayName || this.colorGroupKey) : '',
            font: { size: 14 }
          },
          visible: hasLegends,
          x: hasLegends ? 1.02 : 0.98,
          y: 0.5,
          orientation: 'v',
          xanchor: hasLegends ? 'left' : 'right',
          yanchor: 'middle',
          font: { size: 12 }
        },
      };

      if (this.chartType === "3D Scatter") {
        return {
          ...baseLayout,
          scene: {
            xaxis: {
              title: { 
                text: this.formatAxisTitle(
                  this.xAxisDisplayName || '',
                  this.getUOMForProperty(this.xAxisColumn)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickangle: -45,
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              ...(this.xAxisScale === 'log' ? { type: 'log' } : {
                type: 'auto',
                tickmode: 'auto',
                nticks: undefined,
                tickformat: this.getFormattedTickFormat('x'),
              }),
            },
            yaxis: {
              title: {
                text: this.formatAxisTitle(
                  (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ").replace(/, /g, "<br>") : ''),
                  this.getUOMForProperty(this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              ...(this.yAxisScale === 'log' ? { type: 'log' } : {
                type: 'auto',
                tickmode: 'auto',
                nticks: undefined,
                tickformat: this.getFormattedTickFormat('y'),
              }),
            },
            zaxis: {
              title: {
                text: this.formatAxisTitle(
                  this.zAxisDisplayName || 'Z',
                  this.getUOMForProperty(this.zAxisColumn)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              tickformat: this.getFormattedTickFormat('z'),
            },
            camera: {
              eye: { x: 1.5, y: 1.5, z: 1.5 },
              center: { x: 0, y: 0, z: 0 },
              up: { x: 0, y: 0, z: 1 }
            },
            aspectmode: 'auto',
            aspectratio: { x: 1, y: 1, z: 1 }
          },
        };
      }

      return {
        xaxis: {
          title: { 
            text: this.formatAxisTitle(
              this.xAxisDisplayName || '',
              this.getUOMForProperty(this.xAxisColumn)
            ),
              standoff: 30, // Increased standoff for better separation from scale
            font: { size: 12 }
          },
          tickangle: -45,
          tickfont: { size: 10 },
          automargin: true,
          autorange: true,
          rangemode: 'normal',
          ...(this.xAxisScale === 'log' ? { type: 'log' } : {
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat('x'),
          }),
        },
        yaxis: {
          title: {
            text: (this.chartType === "Histogram")
              ? "No. of Samples"
              : (() => {
                  const displayName = this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ").replace(/, /g, "<br>") : '';
                  const columnName = this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null;
                  const uom = this.getUOMForProperty(columnName);
                  return this.formatAxisTitle(displayName, uom);
                })(),
              standoff: 30, // Increased standoff for better separation from scale
            font: { size: 12 }
          },
          tickfont: { size: 10 },
          automargin: true,
          autorange: true,
          rangemode: 'normal',
          ...(this.yAxisScale === 'log' ? { type: 'log' } : {
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat('y'),
          }),
        },
        margin: { l: 55, r: rightMargin, b: 70, t: 100 },
        height: this.chartHeight,
        autosize: true,
        ...(this.chartType === "Histogram" ? {
          plot_bgcolor: this.isDarkMode ? 'transparent' : '#e5ecf6',
          paper_bgcolor: this.isDarkMode ? 'transparent' : '#ffffff',
          // Remove unused white space below histogram
          margin: { l: 55, r: rightMargin, b: 50, t: 100 },
          // Ensure proper height for histogram - use the calculated height
          height: this.chartHeight,
        } : {}),
        legend: {
          title: {
            text: this.colorGroupKey && this.colorGroupKey.trim() !== '' ? (this.colorGroupDisplayName || this.colorGroupKey) : '',
            font: { size: 14 }
          },
          visible: this.colorGroupKey && this.colorGroupKey.trim() !== '',
          x: 1.02,
          y: 0.5,
          orientation: 'v',
          xanchor: 'left',
          yanchor: 'middle',
          font: { size: 12 }
        },
        ...baseLayout,
      };
    },
    generateModalLayout() {
      // Check if there are legends to determine right margin
      const hasLegends = this.chartType === "Histogram" 
        ? (this.hasValidXColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '')
        : this.chartType === "Line Plot" && this.tableData && this.tableData.length > 0
          ? (this.colorGroupKey && this.colorGroupKey.trim() !== '')
          : (this.hasValidXColorGroupData && this.hasValidYColorGroupData && this.colorGroupKey && this.colorGroupKey.trim() !== '');
      const rightMargin = hasLegends ? 120 : 50;
      
      // Simple responsive layout for modal
      const baseLayout = {
        autosize: true,
        height: this.calculateModalChartHeight() || 600,
        margin: { l: 50, r: rightMargin, b: 50, t: 50 },
        legend: {
          title: {
            text: hasLegends ? (this.colorGroupDisplayName || this.colorGroupKey) : '',
            font: { size: 14 }
          },
          visible: hasLegends,
          x: hasLegends ? 1.02 : 0.98,
          y: 0.5,
          orientation: 'v',
          xanchor: hasLegends ? 'left' : 'right',
          yanchor: 'middle',
          font: { size: 12 }
        },
      };

      if (this.chartType === "3D Scatter") {
        return {
          ...baseLayout,
          scene: {
            xaxis: {
              title: { 
                text: this.formatAxisTitle(
                  this.xAxisDisplayName || '',
                  this.getUOMForProperty(this.xAxisColumn)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickangle: -45,
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              ...(this.xAxisScaleModal === 'log' ? { type: 'log' } : {
                type: 'auto',
                tickmode: 'auto',
                nticks: undefined,
                tickformat: this.getFormattedTickFormat('x'),
              }),
            },
            yaxis: {
              title: {
                text: this.formatAxisTitle(
                  (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ").replace(/, /g, "<br>") : ''),
                  this.getUOMForProperty(this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              ...(this.yAxisScaleModal === 'log' ? { type: 'log' } : {
                type: 'auto',
                tickmode: 'auto',
                nticks: undefined,
                tickformat: this.getFormattedTickFormat('y'),
              }),
            },
            zaxis: {
              title: {
                text: this.formatAxisTitle(
                  this.zAxisDisplayName || 'Z',
                  this.getUOMForProperty(this.zAxisColumn)
                ),
                standoff: 30,
                font: { size: 12 }
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              rangemode: 'normal',
              tickformat: this.getFormattedTickFormat('z'),
            },
            camera: {
              eye: { x: 1.5, y: 1.5, z: 1.5 },
              center: { x: 0, y: 0, z: 0 },
              up: { x: 0, y: 0, z: 1 }
            },
            aspectmode: 'auto',
            aspectratio: { x: 1, y: 1, z: 1 }
          },
        };
      }

      return {
        ...baseLayout,
        xaxis: {
          title: { 
            text: this.formatAxisTitle(
              this.xAxisDisplayName || '',
              this.getUOMForProperty(this.xAxisColumn)
            ),
            standoff: 30,
            font: { size: 12 }
          },
          tickangle: -45,
          tickfont: { size: 10 },
          automargin: true,
          autorange: true,
          rangemode: 'normal',
          ...(this.xAxisScaleModal === 'log' ? { type: 'log' } : {
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat('x'),
          }),
        },
        yaxis: {
          title: {
            text: (this.chartType === "Histogram")
              ? "No. of Samples"
              : this.formatAxisTitle(
                  (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0 ? this.yAxisDisplayNames.join(", ").replace(/, /g, "<br>") : ''),
                  this.getUOMForProperty(this.yAxisColumns && this.yAxisColumns.length > 0 ? this.yAxisColumns[0] : null)
                ),
            standoff: 30,
            font: { size: 12 }
          },
          tickfont: { size: 10 },
          automargin: true,
          autorange: true,
          rangemode: 'normal',
          ...(this.yAxisScaleModal === 'log' ? { type: 'log' } : {
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat('y'),
          }),
        },
        ...(this.chartType === "Histogram" ? {
          plot_bgcolor: this.isDarkMode ? 'transparent' : '#e5ecf6',
          paper_bgcolor: this.isDarkMode ? 'transparent' : '#ffffff',
        } : {}),
      };
    },
    generateConfig() {
      return {
        responsive: true,
        displaylogo: false,
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d', 'pan2d', "resetscale", "zoom", "toImage"],
        modeBarButtonsToAdd: [
          {
            name: "Download plot as a png",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${this.camIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadPNG,
          },
          {
            name: "Expand",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${Expand}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.openModal,
          },
          {
            name: "Download CSV",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${downloadIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadCSV,
          },
        ],
        // Add autosize configuration for better responsiveness
        autosize: true,
        // Set proper scaling for responsive behavior
        useResizeHandler: true,
        // Maintain aspect ratio
        maintainAspectRatio: true,
      };
    },
    generateModalConfig() {
      return {
        responsive: true,
        displaylogo: false,
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d', 'pan2d', "resetscale", "zoom", "toImage"],
        modeBarButtonsToAdd: [
          {
            name: "Download plot as a png",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${this.camIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadPNG,
          },
          {
            name: "Download CSV",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${downloadIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadCSV,
          },
        ],
        autosize: true,
        useResizeHandler: true,
        maintainAspectRatio: true,
      };
    },
    getTraceType() {
      switch (this.chartType) {
        case "Line Plot": return "scatter";
        case "Bar Chart": return "bar";
        case "Histogram": return "histogram";
        case "Bubble": return "scatter";
        case "3D Scatter": return "scatter3d";
        case "Scatter Plot": return "scatter";
        case "2D Histogram": return "histogram2d";
        case "Heatmap": return "heatmap";
        case "Treemap": return "treemap";
        default: return "scatter";
      }
    },
    getTraceMode() {
      switch (this.chartType) {
        case "Line Plot": 
          // Remove markers for line plots to improve performance and cleaner appearance
          return "lines";
        case "Bubble": return "markers";
        case "Scatter Plot": return "markers";
        case "3D Scatter": return "markers";
        case "Bar Chart": return undefined;
        case "Histogram": return undefined;
        case "2D Histogram": return undefined;
        case "Heatmap": return undefined;
        case "Treemap": return undefined;
        default: return "lines";
      }
    },
    getLayoutTitle() {
      const titles = {
        "Line Plot": "Line Plot",
        "Bar Plot": "Bar Plot",
        "Histogram": "Histogram Plot",
        "Bubble": "Bubble Plot",
        "3D Scatter": "3D Scatter Plot",
        "Scatter Plot": "Scatter Plot",
        "2D Histogram": "2D Histogram Plot",
        "Heatmap": "Heatmap Plot",
        "Treemap": "Treemap Plot"
      };
      return titles[this.chartType] || "";
    },
    calculateBinSize(data, bins) {
      // Handle edge cases
      if (!data || data.length === 0 || !bins || bins < 1) {
        return 1;
      }
      
      // Filter out null, undefined, and empty string values
      const validData = data.filter(value => 
        value !== null && 
        value !== undefined && 
        value !== ''
      );
      
      if (validData.length === 0) {
        return 1;
      }
      
      // For numeric data, use proper bin calculation
      const numericData = validData.filter(value => typeof value === 'number' && !isNaN(value));
      if (numericData.length > 0) {
        const minValue = Math.min(...numericData);
        const maxValue = Math.max(...numericData);
        const range = maxValue - minValue;
        
        // Prevent division by zero
        if (range === 0) {
          return 1;
        }
        
        // Use the exact number of bins requested - no minimum bin size constraint
        const binSize = range / bins;
        
        return binSize;
      }
      
      // For non-numeric data (categorical), use unique values
      const uniqueValues = [...new Set(validData)];
      const uniqueCount = uniqueValues.length;
      
      // For categorical data, use the requested number of bins
      const optimalBins = Math.min(bins, uniqueCount);
      
      return Math.max(uniqueCount / optimalBins, 1);
    },
    
    // Method to detect propagation patterns in data
    detectPropagationPattern(xData, yData) {
      if (!xData || !yData || xData.length !== yData.length || xData.length < 2) {
        return { type: 'unknown', confidence: 0 };
      }
      
      // Filter out invalid data points
      const validPairs = [];
      for (let i = 0; i < xData.length; i++) {
        const x = xData[i];
        const y = yData[i];
        if (x !== null && x !== undefined && x !== '' && 
            y !== null && y !== undefined && y !== '' && 
            !isNaN(y) && typeof x === 'number') {
          validPairs.push({ x, y });
        }
      }
      
      if (validPairs.length < 2) {
        return { type: 'unknown', confidence: 0 };
      }
      
      // Sort by x values - handle both numeric and string data
      validPairs.sort((a, b) => {
        // If both are numbers, sort numerically
        if (typeof a.x === 'number' && typeof b.x === 'number') {
          return a.x - b.x;
        }
        // If both are strings, use simple string comparison
        if (typeof a.x === 'string' && typeof b.x === 'string') {
          return a.x.localeCompare(b.x);
        }
        // Mixed types: convert to strings and compare
        return String(a.x).localeCompare(String(b.x));
      });
      
      // Calculate trend
      let increasingCount = 0;
      let decreasingCount = 0;
      
      for (let i = 1; i < validPairs.length; i++) {
        const prevY = validPairs[i - 1].y;
        const currY = validPairs[i].y;
        
        if (currY > prevY) {
          increasingCount++;
        } else if (currY < prevY) {
          decreasingCount++;
        }
      }
      
      const totalComparisons = validPairs.length - 1;
      const increasingRatio = increasingCount / totalComparisons;
      const decreasingRatio = decreasingCount / totalComparisons;
      
      if (increasingRatio > 0.7) {
        return { type: 'increasing', confidence: increasingRatio };
      } else if (decreasingRatio > 0.7) {
        return { type: 'decreasing', confidence: decreasingRatio };
      } else {
        return { type: 'fluctuating', confidence: Math.max(increasingRatio, decreasingRatio) };
      }
    },
    openModal() {
      this.isModalOpen = true;
      
      this.$nextTick(() => {
        // Ensure the modal is fully rendered before plotting
        this.$nextTick(() => {
          this.plotModalChart();
        });
      });
    },
    closeModal() {
      this.isModalOpen = false;
    },
    
    

    checkSidebarState() {
      const currentSidebarState = localStorage.getItem('sidebarState');
      if (this.previousSidebarState !== currentSidebarState) {
        this.previousSidebarState = currentSidebarState;
      }
    },
    setupsidebarStateCheck() {
      this.checkSidebarState();
      this.sidebarStateCheckInterval = setInterval(this.checkSidebarState, 100);
    },
    removeSidebarStateCheck() {
      if (this.sidebarStateCheckInterval) {
        clearInterval(this.sidebarStateCheckInterval);
      }
    },
    // Method to handle filter drawer state changes
    handleFilterDrawerChange() {
      this.$nextTick(() => {
        // Redraw chart when filter drawer state changes
        this.plotChart();
      });
    },






    // Setup legend click event handler for Plotly charts
    setupLegendClickHandler(isModal = false) {
      let chartRef = isModal ? this.$refs[this.modalChartId] : this.$refs[this.chartId];
      
      // Handle case where chartRef might be an array
      if (Array.isArray(chartRef)) {
        chartRef = chartRef[0];
      }
      
      if (!chartRef) {
        console.warn('Chart container not found for legend click handler', {
          isModal,
          modalChartId: this.modalChartId,
          chartId: this.chartId,
          refs: Object.keys(this.$refs)
        });
        return;
      }

      // Use a more robust approach with Plotly's event handling
      try {
        // Remove any existing event listeners to prevent duplicates
        if (chartRef.removeAllListeners) {
          chartRef.removeAllListeners('plotly_legendclick');
        }
        
        // Add legend click event listener using Plotly's event system
        if (chartRef.on) {
          chartRef.on('plotly_legendclick', (event) => {
            // Get the clicked legend item
            const clickedItem = event.curveNumber;
            const traces = event.data;
            
            if (clickedItem !== undefined && traces && traces[clickedItem]) {
              const clickedTrace = traces[clickedItem];
              
              // Toggle visibility for only the clicked trace
              const updates = {};
              updates[`visible[${clickedItem}]`] = !clickedTrace.visible;
              
              // Apply the visibility change
              Plotly.restyle(chartRef, updates);
            }
          });
        } else {
          // Fallback: use document-level event handling
          console.warn('Using fallback event handling for legend clicks');
          const chartElement = chartRef;
          if (chartElement && chartElement.addEventListener) {
            chartElement.addEventListener('plotly_legendclick', (event) => {
              const clickedItem = event.detail.curveNumber;
              const traces = event.detail.data;
              
              if (clickedItem !== undefined && traces && traces[clickedItem]) {
                const clickedTrace = traces[clickedItem];
                
                // Toggle visibility for only the clicked trace
                const updates = {};
                updates[`visible[${clickedItem}]`] = !clickedTrace.visible;
                
                // Apply the visibility change
                Plotly.restyle(chartRef, updates);
              }
            });
          }
        }
      } catch (error) {
        console.error('Error setting up legend click handler:', error);
      }
    },
    

  },
  mounted() {
    // Initialize the sidebar state check
    this.previousSidebarState = localStorage.getItem('sidebarState');
    
    this.$nextTick(() => {
      // Simple plot creation
      this.plotChart();
      
      // Check if we're transitioning from edit to create mode
      this.detectEditToCreateTransition();
    });
    
    // Listen for filter drawer state changes
    eventBus.on("filterDrawerToggled", this.handleFilterDrawerChange);
  },
  beforeUnmount() {
    this.removeSidebarStateCheck();
    
    // Remove event listeners
    eventBus.off("filterDrawerToggled", this.handleFilterDrawerChange);
  },
};
</script>

<style scoped>
/* Smooth height transitions for edit mode */
.studio-chart-wrapper.create-edit {
  transition: height 0.4s ease-out;
}


/* Ensure the modal chart container can accommodate the calculated dimensions */
.modal-chart-container {
  display: block;
  width: 100% !important;
  height: 100% !important;
  min-height: 400px;
  position: relative;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden;
  box-sizing: border-box;
  /* Ensure maximum space utilization */
  flex: 1;
  max-width: 100%;
  max-height: 100%;
  /* Force full space usage */
  min-width: 100%;
  min-height: 100%;
}

/* Ensure Plotly container is visible and properly sized */
.modal-chart-container .js-plotly-plot {
  width: 100% !important;
  height: 100% !important;
  min-width: 300px;
  min-height: 300px;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
}

/* Ensure the plotly container itself is visible */
.modal-chart-container .plotly {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
}

/* Remove any default spacing from plotly elements */
.modal-chart-container .plotly .main-svg {
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

/* Force plotly to use full container space */
.modal-chart-container .plotly .plot-container {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* Responsive modal chart styles */
@media (max-width: 1200px) {
  .modal-chart-container {
    min-height: 450px;
  }
}

@media (max-width: 768px) {
  .modal-chart-container {
    min-height: 400px;
  }
}

@media (max-width: 480px) {
  .modal-chart-container {
    min-height: 350px;
  }
}
</style>


